import * as React from 'react';

import {
  closestCenter,
  DndContext,
  type DragEndEvent,
  type DragStartEvent,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  CalendarDateTime,
  getLocalTimeZone,
  now,
  Time,
  toCalendarDate,
  toTime,
  toZoned,
  type ZonedDateTime,
} from '@internationalized/date';
import {
  Button,
  cn,
  Row,
  SearchField,
  SmartDatePicker,
} from '@rematter/ui-web';
import {
  LucideChevronDown,
  LucideChevronRight,
  LucideGripVertical,
  LucidePlus,
  LucideX,
} from 'lucide-react';

import type { EvaluatedTierData } from '../../_graphql/queryEvaluateFormulas';
import { useTranslations } from '../../i18n/useTranslations';
import type { ItemPricingRecord, VariableRecord } from '../../types/pricing';

import { BufferedTimeField } from './BufferedTimeField';
import { FormulaBar } from './FormulaBar';
import type {
  AddItemPricingInput,
  BatchSaveItem,
} from './hooks/useEditablePricingTable';
import { useEditablePricingTable } from './hooks/useEditablePricingTable';
import { useGridFocus } from './hooks/useGridFocus';
import { useInfiniteScroll } from './hooks/useInfiniteScroll';
import { usePricingSearch } from './hooks/usePricingSearch';
import { type SelectedCell } from './hooks/useProductPricingColumns';
import { PricingBulkToolbar } from './PricingBulkToolbar';
import { PricingSaveToolbar } from './PricingSaveToolbar';
import { SpreadsheetRow } from './SpreadsheetRow';
import { SpreadsheetTable } from './SpreadsheetTable';
import { ValidationErrorsPanel } from './ValidationErrorsPanel';

type PriceListViewProps = {
  priceListId: string;
  itemPricings: ItemPricingRecord[];
  pricingsFetching: boolean;
  variables: VariableRecord[];
  evaluatedPrices: Record<string, EvaluatedTierData>;
  selectedCell: SelectedCell | null;
  setSelectedCell: (cell: SelectedCell | null) => void;

  onBatchSave: (params: {
    listId: string;
    items: BatchSaveItem[];
  }) => Promise<unknown>;
  onAddItemPricing: (input: AddItemPricingInput) => Promise<unknown>;
  onDeleteItemPricing: (params: { itemPricingId: string }) => Promise<unknown>;
  onReorderItemPricings?: (params: {
    itemPricingIds: string[];
  }) => Promise<unknown>;
  onRefetchPricings: () => void;
  onRefetchEvaluated: () => void;

  isReadOnly?: boolean;
  pricingMode?: 'Standard' | 'CRV';

  asOfDateTime?: ZonedDateTime | null;
  onAsOfDateTimeChange?: (dt: ZonedDateTime | null) => void;
  isAsOfReadOnly?: boolean;

  toolbarLeft?: React.ReactNode;
  toolbarRight?: React.ReactNode;
  emptyTitle: string;
  emptyDescription: string;
  gridAriaLabel: string;
};

type GroupHeaderRowProps = {
  groupName: string;
  itemCount: number;
  isCollapsed: boolean;
  onToggle: () => void;
  colSpan: number;
  isDndDisabled: boolean;
};

//
// ⚛️ ⚛️ ⚛️ GROUP HEADER ROW ⚛️ ⚛️ ⚛️
//
const GroupHeaderRow = ({
  groupName,
  itemCount,
  isCollapsed,
  onToggle,
  colSpan,
  isDndDisabled,
}: GroupHeaderRowProps) => {
  const { attributes, listeners, setNodeRef, transform, transition } =
    useSortable({
      id: `group:${groupName}`,
      disabled: isDndDisabled,
    });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <tr
      ref={setNodeRef}
      style={style}
      className="@GroupHeaderRow cursor-pointer bg-neutral/5 hover:bg-neutral/10"
      onClick={onToggle}
    >
      <td colSpan={colSpan} className="border border-neutral/20 px-3 py-1.5">
        <div className="flex items-center gap-2">
          {!isDndDisabled && (
            <div
              className="flex cursor-grab items-center opacity-50"
              {...listeners}
              {...attributes}
              onClick={(e) => e.stopPropagation()}
            >
              <LucideGripVertical className="size-4" />
            </div>
          )}
          {isCollapsed ? (
            <LucideChevronRight className="size-4 text-neutral/60" />
          ) : (
            <LucideChevronDown className="size-4 text-neutral/60" />
          )}
          <span className="text-sm font-semibold text-neutral">
            {groupName}
          </span>
          <span className="text-xs text-neutral/50">({itemCount})</span>
        </div>
      </td>
    </tr>
  );
};

//
// ⚛️ ⚛️ ⚛️ PRICE LIST VIEW ⚛️ ⚛️ ⚛️
//
export const PriceListView = ({
  priceListId,
  itemPricings,
  pricingsFetching: _pricingsFetching,
  variables,
  evaluatedPrices,
  selectedCell,
  setSelectedCell,
  onBatchSave,
  onAddItemPricing,
  onDeleteItemPricing,
  onReorderItemPricings,
  onRefetchPricings,
  onRefetchEvaluated,
  isReadOnly,
  pricingMode,
  asOfDateTime,
  onAsOfDateTimeChange,
  isAsOfReadOnly,
  toolbarLeft,
  toolbarRight,
  emptyTitle: _emptyTitle,
  emptyDescription: _emptyDescription,
  gridAriaLabel,
}: PriceListViewProps) => {
  // Translations
  const t = useTranslations();

  // Refs
  const containerRef = React.useRef<HTMLDivElement>(null);
  const fieldRefs = React.useRef<(HTMLDivElement | null)[]>([]);

  // States
  const [collapsedGroups, setCollapsedGroups] = React.useState<Set<string>>(
    new Set(),
  );
  const [draggingGroup, setDraggingGroup] = React.useState<string | null>(null);
  const [selectedRowIds, setSelectedRowIds] = React.useState<Set<string>>(
    new Set(),
  );

  // Data hooks
  const isCRV = pricingMode === 'CRV';
  const maxColumns = isCRV ? 3 : 7;
  const tz = getLocalTimeZone();
  const sensors = useSensors(useSensor(PointerSensor));

  const {
    pendingChanges,
    adHocEvaluated,
    pendingCount,
    effectiveDate,
    setEffectiveDate,
    effectiveTime,
    setEffectiveTime,
    flatTierRows,
    selectedCellValue,
    selectedCellEvaluatedValue,
    handleCellChange,
    handleFormulaBarChange,
    handleDeleteItemPricing,
    handleCopyItemPricing,
    handleBatchSave,
    validationErrors,
    handleDiscard,
    revertRow,
    addEmptyRow,
    removeLastEmptyRow,
    addTierRow,
    deleteTier,
    reorderItems,
    reorderGroup,
    newInlineRows,
    isInlineRow,
    getInlineRowData,
    handleInlineRowChange,
    pendingFocusRowId,
    setPendingFocusRowId,
  } = useEditablePricingTable({
    pricingsList: itemPricings,
    evaluatedPrices,
    variables,
    selectedCell,
    setSelectedCell,
    pricingMode,
    listId: priceListId,
    batchSave: onBatchSave,
    deleteItemPricing: onDeleteItemPricing,
    addItemPricing: onAddItemPricing,
    reorderItemPricings: onReorderItemPricings,
    refetchItemPricings: onRefetchPricings,
    refetchEvaluatedFormulas: onRefetchEvaluated,
  });

  // Memoized values
  const currentNow = React.useMemo(() => now(tz), [tz]);
  const isHistoricalView =
    !!asOfDateTime && asOfDateTime.compare(currentNow) < 0;
  const effectiveReadOnly =
    isReadOnly || (isReadOnly !== false && isHistoricalView);

  const endOfDayTime = React.useMemo(() => new Time(23, 59), []);
  const displayDateTime = asOfDateTime ?? currentNow;
  const asOfDate = toCalendarDate(displayDateTime);
  const asOfTime = React.useMemo(
    () => (asOfDateTime ? toTime(displayDateTime) : endOfDayTime),
    [asOfDateTime, displayDateTime, endOfDayTime],
  );

  const activeRows = React.useMemo(
    () => flatTierRows.filter((row) => !row.isPendingDelete),
    [flatTierRows],
  );

  const existingItemIds = React.useMemo(() => {
    const ids = new Set<string>();
    for (const row of activeRows) {
      if (row.itemId && row.isFirstTierOfProduct) ids.add(row.itemId);
    }
    return ids;
  }, [activeRows]);

  const { searchQuery, setSearchQuery, filteredRows, isFiltered } =
    usePricingSearch(activeRows);

  const effectiveCollapsedGroups = React.useMemo(() => {
    if (!draggingGroup) return collapsedGroups;
    const all = new Set(collapsedGroups);
    for (const row of filteredRows) {
      if (row.id === 'empty-bottom' || row.isNewRow) continue;
      all.add(row.itemGroupName ?? t.pricing.groups.ungrouped);
    }
    return all;
  }, [collapsedGroups, draggingGroup, filteredRows, t]);

  const groupBoundaries = React.useMemo(() => {
    const countMap = new Map<string, number>();
    const orderSeen: string[] = [];

    for (const row of filteredRows) {
      if (row.id === 'empty-bottom') continue;
      if (!row.isFirstTierOfProduct) continue;
      const group = row.itemGroupName ?? t.pricing.groups.ungrouped;
      const prev = countMap.get(group);
      if (prev === undefined) {
        orderSeen.push(group);
        countMap.set(group, 1);
      } else {
        countMap.set(group, prev + 1);
      }
    }

    return orderSeen.map((groupName) => ({
      groupName,
      count: countMap.get(groupName) ?? 0,
    }));
  }, [filteredRows, t]);

  const hasGroups = groupBoundaries.length > 1;

  const expandedRows = React.useMemo(() => {
    if (!hasGroups || isFiltered) return filteredRows;
    const seenCollapsedGroups = new Set<string>();
    return filteredRows.filter((row) => {
      if (row.id === 'empty-bottom' || row.isNewRow) return true;
      const group = row.itemGroupName ?? t.pricing.groups.ungrouped;
      if (!effectiveCollapsedGroups.has(group)) return true;
      if (!seenCollapsedGroups.has(group)) {
        seenCollapsedGroups.add(group);
        return true;
      }
      return false;
    });
  }, [filteredRows, hasGroups, isFiltered, effectiveCollapsedGroups, t]);

  const groupedRows = React.useMemo(() => {
    if (!hasGroups || isFiltered) return null;
    const groups: {
      groupName: string;
      rows: { row: (typeof expandedRows)[number]; rowIndex: number }[];
    }[] = [];
    const trailingRows: {
      row: (typeof expandedRows)[number];
      rowIndex: number;
    }[] = [];
    let current: (typeof groups)[number] | null = null;
    let currentGroupName: string | null = null;

    for (let i = 0; i < expandedRows.length; i++) {
      const row = expandedRows[i];
      if (row.id === 'empty-bottom') {
        trailingRows.push({ row, rowIndex: i });
        continue;
      }
      const group = row.itemGroupName ?? t.pricing.groups.ungrouped;
      if (group !== currentGroupName) {
        current = { groupName: group, rows: [] };
        groups.push(current);
        currentGroupName = group;
      }
      current?.rows.push({ row, rowIndex: i });
    }
    return { groups, trailingRows };
  }, [expandedRows, hasGroups, isFiltered, t]);

  const { visibleCount, hasMore, sentinelRef } = useInfiniteScroll(
    expandedRows.length,
  );
  const visibleRows = React.useMemo(
    () => expandedRows.slice(0, visibleCount),
    [expandedRows, visibleCount],
  );

  const isDndDisabled = effectiveReadOnly || pendingCount > 0;

  const allVisibleIds = React.useMemo(() => {
    const ids = new Set<string>();
    for (const r of visibleRows) ids.add(r.itemPricingId);
    return ids;
  }, [visibleRows]);

  const allSelected =
    allVisibleIds.size > 0 &&
    [...allVisibleIds].every((id) => selectedRowIds.has(id));

  const groupSortableIds = React.useMemo(() => {
    if (!hasGroups) return [];
    const ids: string[] = [];
    const seen = new Set<string>();
    for (const row of visibleRows) {
      if (row.id === 'empty-bottom' || row.isNewRow) continue;
      const group = row.itemGroupName ?? t.pricing.groups.ungrouped;
      if (!seen.has(group)) {
        ids.push(`group:${group}`);
        seen.add(group);
      }
    }
    return ids;
  }, [visibleRows, hasGroups, t]);

  const perGroupSortableIds = React.useMemo(() => {
    const map = new Map<string, string[]>();
    const seen = new Set<string>();
    for (const row of visibleRows) {
      if (row.id === 'empty-bottom' || row.isNewRow) continue;
      const group = row.itemGroupName ?? t.pricing.groups.ungrouped;
      if (!seen.has(row.itemPricingId)) {
        let ids = map.get(group);
        if (!ids) {
          ids = [];
          map.set(group, ids);
        }
        ids.push(row.itemPricingId);
        seen.add(row.itemPricingId);
      }
    }
    return map;
  }, [visibleRows, t]);

  const flatSortableItems = React.useMemo(() => {
    if (hasGroups) return [];
    const items: string[] = [];
    const seen = new Set<string>();
    for (const row of visibleRows) {
      if (row.id === 'empty-bottom' || row.isNewRow) continue;
      if (!seen.has(row.itemPricingId)) {
        items.push(row.itemPricingId);
        seen.add(row.itemPricingId);
      }
    }
    return items;
  }, [visibleRows, hasGroups]);

  const itemGroupMap = React.useMemo(() => {
    const map = new Map<string, string>();
    for (const row of filteredRows) {
      if (row.id === 'empty-bottom' || row.isNewRow) continue;
      map.set(
        row.itemPricingId,
        row.itemGroupName ?? t.pricing.groups.ungrouped,
      );
    }
    return map;
  }, [filteredRows, t]);

  const columnHeaders = React.useMemo(() => {
    if (isCRV) {
      return [
        { key: 'drag', label: '', width: 'w-10' },
        { key: 'product', label: t.pricing.itemPricings.table.columns.product },
        {
          key: 'unit',
          label: t.pricing.itemPricings.table.columns.pricingUnit,
        },
        {
          key: 'scrapValue',
          label: t.pricing.itemPricings.table.columns.scrapValue,
        },
        { key: 'actions', label: '', width: 'w-10' },
      ];
    }
    return [
      { key: 'drag', label: '', width: 'w-10' },
      {
        key: 'product',
        label: t.pricing.itemPricings.table.columns.product,
        width: 'w-[40%]',
      },
      {
        key: 'unit',
        label: t.pricing.itemPricings.table.columns.pricingUnit,
        width: 'w-[10%]',
      },
      {
        key: 'minWeight',
        label: t.pricing.itemPricings.table.columns.minWeight,
        width: 'w-[8%]',
        align: 'text-right',
      },
      {
        key: 'maxWeight',
        label: t.pricing.itemPricings.table.columns.maxAmount,
        width: 'w-[8%]',
        align: 'text-right',
      },
      {
        key: 'minPrice',
        label: t.pricing.itemPricings.table.columns.minPrice,
        width: 'w-[10%]',
        align: 'text-right',
      },
      {
        key: 'targetPrice',
        label: t.pricing.itemPricings.table.columns.targetPrice,
        width: 'w-[12%]',
        align: 'text-right',
      },
      {
        key: 'maxPrice',
        label: t.pricing.itemPricings.table.columns.maxPrice,
        width: 'w-[10%]',
        align: 'text-right',
      },
      { key: 'actions', label: '', width: 'w-10' },
    ];
  }, [t, isCRV]);

  // Callbacks
  const handleAsOfDateChange = React.useCallback(
    (date: import('@internationalized/date').CalendarDate | undefined) => {
      if (!onAsOfDateTimeChange || !date) return;
      const time = asOfDateTime ? toTime(asOfDateTime) : endOfDayTime;
      onAsOfDateTimeChange(
        toZoned(
          new CalendarDateTime(
            date.year,
            date.month,
            date.day,
            time.hour,
            time.minute,
            time.second,
          ),
          tz,
        ),
      );
    },
    [onAsOfDateTimeChange, asOfDateTime, currentNow, tz],
  );

  const handleAsOfTimeChange = React.useCallback(
    (time: Time) => {
      if (!onAsOfDateTimeChange) return;
      const date = asOfDateTime
        ? toCalendarDate(asOfDateTime)
        : toCalendarDate(currentNow);
      onAsOfDateTimeChange(
        toZoned(
          new CalendarDateTime(
            date.year,
            date.month,
            date.day,
            time.hour,
            time.minute,
            0,
          ),
          tz,
        ),
      );
    },
    [onAsOfDateTimeChange, asOfDateTime, currentNow, tz],
  );

  const skipCell = React.useCallback(
    (cell: { row: number; col: number }) => {
      const row = visibleRows[cell.row];
      if (!row) return true;
      if ((cell.col === 0 || cell.col === 1) && !row.isFirstTierOfProduct)
        return true;
      return false;
    },
    [visibleRows],
  );

  const {
    focusState,
    editingState,
    handleContainerKeyDown,
    getCommonProps,
    exitEditing,
    exitEditingAndNavigate,
  } = useGridFocus({
    maxColumns,
    rowCount: visibleRows.length,
    onAddRow: addEmptyRow,
    onRemoveLastEmptyRow: removeLastEmptyRow,
    skipCell,
    containerRef,
    fieldRefs,
    editOnFocus: true,
  });

  const focusedRow = visibleRows[focusState.row];

  // Memoized values
  const focusedPriceField = React.useMemo(() => {
    if (!focusedRow) return null;
    if (isCRV) return null;
    const colToField: Record<number, 'targetPrice' | 'minPrice' | 'maxPrice'> =
      { 4: 'minPrice', 5: 'targetPrice', 6: 'maxPrice' };
    const field = colToField[focusState.col];
    if (!field) return null;
    return { tierId: focusedRow.id, field };
  }, [focusedRow, focusState.col, isCRV]);

  // Callbacks
  const toggleGroup = React.useCallback((groupName: string) => {
    setCollapsedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(groupName)) {
        next.delete(groupName);
      } else {
        next.add(groupName);
      }
      return next;
    });
  }, []);

  const handleDragStart = React.useCallback((event: DragStartEvent) => {
    const activeId = String(event.active.id);
    if (activeId.startsWith('group:')) {
      setDraggingGroup(activeId.replace('group:', ''));
    }
  }, []);

  const handleDragEnd = React.useCallback(
    (event: DragEndEvent) => {
      setDraggingGroup(null);
      const { active, over } = event;
      if (!over || active.id === over.id) return;
      const activeId = String(active.id);
      const overId = String(over.id);

      if (activeId.startsWith('group:') && overId.startsWith('group:')) {
        reorderGroup(
          activeId.replace('group:', ''),
          overId.replace('group:', ''),
        );
      } else if (
        !activeId.startsWith('group:') &&
        !overId.startsWith('group:')
      ) {
        if (itemGroupMap.get(activeId) === itemGroupMap.get(overId)) {
          reorderItems(activeId, overId);
        }
      }
    },
    [reorderItems, reorderGroup, itemGroupMap],
  );

  const handleToggleRowSelection = React.useCallback(
    (itemPricingId: string) => {
      setSelectedRowIds((prev) => {
        const next = new Set(prev);
        if (next.has(itemPricingId)) {
          next.delete(itemPricingId);
        } else {
          next.add(itemPricingId);
        }
        return next;
      });
    },
    [],
  );

  const handleSelectAll = React.useCallback(() => {
    setSelectedRowIds(allSelected ? new Set() : new Set(allVisibleIds));
  }, [allSelected, allVisibleIds]);

  const handleBulkDelete = React.useCallback(() => {
    for (const id of selectedRowIds) {
      handleDeleteItemPricing(id);
    }
    setSelectedRowIds(new Set());
  }, [selectedRowIds, handleDeleteItemPricing]);

  // Effects
  /** Clear row selection when search filter is active */
  React.useEffect(() => {
    if (isFiltered) setSelectedRowIds(new Set());
  }, [isFiltered]);

  /** Focus pending row after it becomes visible */
  React.useEffect(() => {
    if (!pendingFocusRowId) return;
    const rowIndex = visibleRows.findIndex((r) => r.id === pendingFocusRowId);
    if (rowIndex === -1) return;
    setPendingFocusRowId(null);
    const targetCol = isCRV ? 2 : 5;
    const cellEl = fieldRefs.current[rowIndex * maxColumns + targetCol];
    if (!cellEl) return;
    setTimeout(() => {
      cellEl.focus();
      cellEl.click();
    }, 50);
  }, [
    pendingFocusRowId,
    visibleRows,
    isCRV,
    maxColumns,
    fieldRefs,
    setPendingFocusRowId,
  ]);

  /** Sync focused price field to selected cell */
  React.useEffect(() => {
    if (focusedPriceField) {
      setSelectedCell(focusedPriceField);
    } else {
      setSelectedCell(null);
    }
  }, [focusedPriceField, setSelectedCell]);

  return (
    <>
      <div className="flex flex-wrap items-center gap-2 py-2">
        {toolbarLeft}
        <SearchField
          value={searchQuery}
          onChange={(value) => setSearchQuery(value ?? '')}
          placeholder={t.pricing.search.placeholder}
          className="w-40"
        />
        <div className="flex-1" />
        {onAsOfDateTimeChange && (
          <Row gap1>
            <p className="mr-1 text-sm text-neutral/70">
              {t.pricing.customPriceLists.detail.subtitle.startsOn}
            </p>
            <SmartDatePicker
              value={asOfDate}
              onChange={handleAsOfDateChange}
              isReadOnly={isAsOfReadOnly}
              className="w-48"
            />
            <BufferedTimeField
              aria-label={t.pricing.asOf.timeLabel}
              value={asOfTime}
              onChange={handleAsOfTimeChange}
              isReadOnly={isAsOfReadOnly}
            />
            {asOfDateTime && !isAsOfReadOnly && (
              <Button
                variant="ghost"
                color="neutral"
                icon={<LucideX size={14} />}
                onPress={() => onAsOfDateTimeChange(null)}
                aria-label={t.pricing.asOf.clearButton}
              />
            )}
          </Row>
        )}
        {toolbarRight}
      </div>

      <div className="flex flex-col">
        <FormulaBar
          value={selectedCellValue}
          evaluatedValue={selectedCellEvaluatedValue}
          fieldLabel={selectedCell?.field ?? null}
          onChange={effectiveReadOnly ? undefined : handleFormulaBarChange}
          variables={variables}
          isDisabled={effectiveReadOnly}
        />

        <ValidationErrorsPanel
          itemPricings={itemPricings}
          evaluatedPrices={evaluatedPrices}
          pricingMode={pricingMode as 'Standard' | 'CRV' | undefined}
        />

        <div
          ref={containerRef}
          tabIndex={0}
          className="outline-none"
          onKeyDown={handleContainerKeyDown}
          aria-label={gridAriaLabel}
        >
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragStart={handleDragStart}
            onDragEnd={handleDragEnd}
            onDragCancel={() => setDraggingGroup(null)}
          >
            <SortableContext
              items={hasGroups ? groupSortableIds : flatSortableItems}
              strategy={verticalListSortingStrategy}
            >
              <SpreadsheetTable>
                <thead>
                  <tr>
                    {columnHeaders.map((col) => (
                      <th
                        key={col.key}
                        className={cn(
                          'border border-neutral/20 bg-[#e5e7eb] text-left text-sm font-semibold text-neutral',
                          col.key === 'drag' ? 'px-1 py-0' : 'p-2',
                          col.width ?? '',
                          'align' in col && col.align,
                        )}
                      >
                        {col.key === 'drag' && !effectiveReadOnly ? (
                          <div className="flex items-center justify-center gap-0.5">
                            <input
                              type="checkbox"
                              checked={allSelected}
                              onChange={handleSelectAll}
                              className="size-3.5 cursor-pointer accent-primary"
                            />
                            <div className="size-4" />
                          </div>
                        ) : (
                          col.label
                        )}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {groupedRows ? (
                    <>
                      {groupedRows.groups.map((group) => {
                        const isCollapsed = effectiveCollapsedGroups.has(
                          group.groupName,
                        );
                        const groupItemCount =
                          groupBoundaries.find(
                            (b) => b.groupName === group.groupName,
                          )?.count ?? 0;
                        return (
                          <SortableContext
                            key={group.groupName}
                            items={
                              perGroupSortableIds.get(group.groupName) ?? []
                            }
                            strategy={verticalListSortingStrategy}
                          >
                            <GroupHeaderRow
                              groupName={group.groupName}
                              itemCount={groupItemCount}
                              isCollapsed={isCollapsed}
                              onToggle={() => toggleGroup(group.groupName)}
                              colSpan={columnHeaders.length}
                              isDndDisabled={isDndDisabled}
                            />
                            {!isCollapsed &&
                              group.rows.map(({ row, rowIndex }) => (
                                <SpreadsheetRow
                                  key={row.id}
                                  row={row}
                                  rowIndex={rowIndex}
                                  focusState={focusState}
                                  editingState={editingState}
                                  pendingChanges={pendingChanges}
                                  evaluatedPrices={evaluatedPrices}
                                  adHocEvaluated={adHocEvaluated}
                                  variables={variables}
                                  isReadOnly={effectiveReadOnly ?? false}
                                  isCRV={isCRV}
                                  inlineRowData={
                                    isInlineRow(row.id)
                                      ? getInlineRowData(row.id)
                                      : null
                                  }
                                  onCellChange={handleCellChange}
                                  onInlineRowChange={handleInlineRowChange}
                                  onExitEditing={exitEditing}
                                  onTabNavigate={exitEditingAndNavigate}
                                  onAddTier={
                                    effectiveReadOnly ? undefined : addTierRow
                                  }
                                  onDeleteTier={
                                    effectiveReadOnly ? undefined : deleteTier
                                  }
                                  onCopy={
                                    effectiveReadOnly
                                      ? undefined
                                      : handleCopyItemPricing
                                  }
                                  onDelete={
                                    effectiveReadOnly
                                      ? undefined
                                      : handleDeleteItemPricing
                                  }
                                  getCommonProps={getCommonProps}
                                  isDragDisabled={isDndDisabled}
                                  isSelected={selectedRowIds.has(
                                    row.itemPricingId,
                                  )}
                                  onSelect={() =>
                                    handleToggleRowSelection(row.itemPricingId)
                                  }
                                  hasValidationError={validationErrors.has(
                                    row.id,
                                  )}
                                  existingItemIds={existingItemIds}
                                />
                              ))}
                          </SortableContext>
                        );
                      })}
                      {groupedRows.trailingRows.map(({ row, rowIndex }) => (
                        <SpreadsheetRow
                          key={row.id}
                          row={row}
                          rowIndex={rowIndex}
                          focusState={focusState}
                          editingState={editingState}
                          pendingChanges={pendingChanges}
                          evaluatedPrices={evaluatedPrices}
                          adHocEvaluated={adHocEvaluated}
                          variables={variables}
                          isReadOnly={effectiveReadOnly ?? false}
                          isCRV={isCRV}
                          inlineRowData={
                            isInlineRow(row.id)
                              ? getInlineRowData(row.id)
                              : null
                          }
                          onCellChange={handleCellChange}
                          onInlineRowChange={handleInlineRowChange}
                          onExitEditing={exitEditing}
                          onTabNavigate={exitEditingAndNavigate}
                          onAddTier={effectiveReadOnly ? undefined : addTierRow}
                          onCopy={
                            effectiveReadOnly
                              ? undefined
                              : handleCopyItemPricing
                          }
                          onDelete={
                            effectiveReadOnly
                              ? undefined
                              : handleDeleteItemPricing
                          }
                          getCommonProps={getCommonProps}
                          isDragDisabled={isDndDisabled}
                          isSelected={selectedRowIds.has(row.itemPricingId)}
                          onSelect={() =>
                            handleToggleRowSelection(row.itemPricingId)
                          }
                          hasValidationError={validationErrors.has(row.id)}
                          existingItemIds={existingItemIds}
                        />
                      ))}
                    </>
                  ) : (
                    visibleRows.map((row, rowIndex) => (
                      <SpreadsheetRow
                        key={row.id}
                        row={row}
                        rowIndex={rowIndex}
                        focusState={focusState}
                        editingState={editingState}
                        pendingChanges={pendingChanges}
                        evaluatedPrices={evaluatedPrices}
                        adHocEvaluated={adHocEvaluated}
                        variables={variables}
                        isReadOnly={effectiveReadOnly ?? false}
                        isCRV={isCRV}
                        inlineRowData={
                          isInlineRow(row.id) ? getInlineRowData(row.id) : null
                        }
                        onCellChange={handleCellChange}
                        onInlineRowChange={handleInlineRowChange}
                        onExitEditing={exitEditing}
                        onTabNavigate={exitEditingAndNavigate}
                        onAddTier={effectiveReadOnly ? undefined : addTierRow}
                        onCopy={
                          effectiveReadOnly ? undefined : handleCopyItemPricing
                        }
                        onDelete={
                          effectiveReadOnly
                            ? undefined
                            : handleDeleteItemPricing
                        }
                        getCommonProps={getCommonProps}
                        isDragDisabled={isDndDisabled}
                        isSelected={selectedRowIds.has(row.itemPricingId)}
                        onSelect={() =>
                          handleToggleRowSelection(row.itemPricingId)
                        }
                        hasValidationError={validationErrors.has(row.id)}
                        existingItemIds={existingItemIds}
                      />
                    ))
                  )}
                </tbody>
              </SpreadsheetTable>
            </SortableContext>
          </DndContext>
          {hasMore && (
            <div
              ref={sentinelRef}
              className="flex items-center justify-center py-4 text-sm text-neutral/50"
            >
              {t.common.loading.loadingMore}
            </div>
          )}
          {!effectiveReadOnly && visibleRows.length > 10 && (
            <div className="sticky bottom-0 z-10 border-t border-neutral/20 bg-white px-4 py-2">
              <Button
                variant="outline"
                color="primary"
                icon={<LucidePlus size={14} />}
                onPress={() => {
                  const lastRow = containerRef.current?.querySelector(
                    'tr[data-row-index]:last-of-type',
                  );
                  lastRow?.scrollIntoView({
                    behavior: 'smooth',
                    block: 'center',
                  });
                  const lastIdx = visibleRows.length - 1;
                  const cell = fieldRefs.current[lastIdx * maxColumns];
                  cell?.focus();
                }}
              >
                {t.pricing.itemPricings.addButton}
              </Button>
            </div>
          )}
        </div>
      </div>

      {!effectiveReadOnly && selectedRowIds.size > 0 && (
        <PricingBulkToolbar
          selectedCount={selectedRowIds.size}
          onDelete={handleBulkDelete}
          onSelectAll={handleSelectAll}
          onClearSelection={() => setSelectedRowIds(new Set())}
        />
      )}

      {!effectiveReadOnly && (
        <PricingSaveToolbar
          pendingCount={pendingCount}
          pendingChanges={pendingChanges}
          flatTierRows={flatTierRows}
          effectiveDate={effectiveDate}
          effectiveTime={effectiveTime}
          onDateChange={setEffectiveDate}
          onTimeChange={setEffectiveTime}
          onSave={handleBatchSave}
          onDiscard={handleDiscard}
          onRevertRow={revertRow}
          newInlineRows={newInlineRows}
          isCRV={isCRV}
        />
      )}
    </>
  );
};
